import React from 'react'
import type {
  LotStoryArtifactDetailV1,
  LotStoryArtifactVersionV1
} from '../../types/lotStory'
import type {
  LotStoryLibraryCompareActions,
  LotStoryLibraryCompareState,
  LotStoryLibraryVersionHistoryRowCompareActions,
  LotStoryLibraryVersionHistoryRowCompareState,
  LotStoryLibraryVersionHistoryRowPreviewActions,
  LotStoryLibraryVersionHistoryRowPreviewState
} from './lotStoryLibraryTypes'

type UseLotStoryLibraryVersionHistoryStateArgs = {
  artifact: LotStoryArtifactDetailV1
  selectedStoryPreviewVersionId: string | null
  onOpenArtifactVersion: (artifact: LotStoryArtifactDetailV1, version: LotStoryArtifactVersionV1) => void
}

type UseLotStoryLibraryVersionHistoryStateResult = {
  compareState: LotStoryLibraryCompareState | null
  compareActions: LotStoryLibraryCompareActions
  rowCompareState: LotStoryLibraryVersionHistoryRowCompareState
  rowCompareActions: LotStoryLibraryVersionHistoryRowCompareActions
  rowPreviewState: LotStoryLibraryVersionHistoryRowPreviewState
  rowPreviewActions: LotStoryLibraryVersionHistoryRowPreviewActions
}

export default function useLotStoryLibraryVersionHistoryState({
  artifact,
  selectedStoryPreviewVersionId,
  onOpenArtifactVersion
}: Readonly<UseLotStoryLibraryVersionHistoryStateArgs>): UseLotStoryLibraryVersionHistoryStateResult {
  const [compareBaseStoryVersionId, setCompareBaseStoryVersionId] = React.useState<string | null>(artifact.version.artifactVersionId)
  const [compareTargetStoryVersionId, setCompareTargetStoryVersionId] = React.useState<string | null>(null)

  React.useEffect(() => {
    const versionIds = new Set(artifact.versions.map((version) => version.artifactVersionId))
    const fallbackBaseVersionId = artifact.version.artifactVersionId
    const nextBaseVersionId = compareBaseStoryVersionId && versionIds.has(compareBaseStoryVersionId)
      ? compareBaseStoryVersionId
      : fallbackBaseVersionId

    if (nextBaseVersionId !== compareBaseStoryVersionId) {
      setCompareBaseStoryVersionId(nextBaseVersionId)
    }

    if (
      compareTargetStoryVersionId &&
      (!versionIds.has(compareTargetStoryVersionId) || compareTargetStoryVersionId === nextBaseVersionId)
    ) {
      setCompareTargetStoryVersionId(null)
    }
  }, [artifact, compareBaseStoryVersionId, compareTargetStoryVersionId])

  const compareBaseStoryVersion = artifact.versions.find((version) => version.artifactVersionId === compareBaseStoryVersionId) ?? null
  const compareTargetStoryVersion = artifact.versions.find((version) => version.artifactVersionId === compareTargetStoryVersionId) ?? null

  const handleClearCompare = () => {
    setCompareTargetStoryVersionId(null)
  }

  const handleSelectCompareBaseVersion = (versionId: string) => {
    setCompareBaseStoryVersionId(versionId)
    setCompareTargetStoryVersionId((current) => current === versionId ? null : current)
  }

  const handleToggleCompareTargetVersion = (versionId: string) => {
    setCompareTargetStoryVersionId((current) => current === versionId ? null : versionId)
  }

  return {
    compareState: compareBaseStoryVersion && compareTargetStoryVersion
      ? {
          artifact,
          selectedStoryPreviewVersionId,
          compareBaseStoryVersion,
          compareTargetStoryVersion
        }
      : null,
    compareActions: {
      onClearCompare: handleClearCompare,
      onOpenArtifactVersion
    },
    rowCompareState: {
      compareBaseStoryVersionId,
      compareTargetStoryVersionId
    },
    rowCompareActions: {
      onSelectCompareBaseVersion: handleSelectCompareBaseVersion,
      onToggleCompareTargetVersion: handleToggleCompareTargetVersion
    },
    rowPreviewState: {
      selectedStoryPreviewVersionId
    },
    rowPreviewActions: {
      onOpenArtifactVersion
    }
  }
}